import React from "react";
import { HiPlus } from "react-icons/hi2";
import SessionCard from "./SessionCard";
import EventCard from "./EventCard";
import RecoveryScheduleCard from "./RecoveryScheduleCard";
import { ScheduledRecovery } from "../../types/scheduledRecovery";
import { SessionVM } from "./scheduleTypes";
import { EventVM } from "./eventVM";
import { getLocalDateString } from "./scheduleDateUtils";
import { ColorMap } from "../../utils/scheduleColors";

interface DayDetailProps {
  date: Date;
  sessions: SessionVM[];
  /** Events on this day (Events.md §4). */
  events?: EventVM[];
  isEventLogged?: (eventId: string) => boolean;
  onSelectEvent?: (eventId: string) => void;
  recoveries?: ScheduledRecovery[];
  onSelectRecovery?: (item: ScheduledRecovery) => void;
  trainingTypeColors?: ColorMap;
  onAssign: () => void;
  onSelectSession: (id: string) => void;
  onDeleteSession: (id: string) => void;
  canChangeDate: (id: string) => boolean;
  onChangeDate: (id: string) => void;
}

const DayDetail: React.FC<DayDetailProps> = ({
  date,
  sessions,
  events = [],
  isEventLogged,
  onSelectEvent,
  recoveries = [],
  onSelectRecovery,
  trainingTypeColors,
  onAssign,
  onSelectSession,
  onDeleteSession,
  canChangeDate,
  onChangeDate,
}) => {
  const loggedCount = sessions.filter(
    (s) => s.status === "complete" && s.linkedTrainingLogID,
  ).length;
  const plannedCount = sessions.filter(
    (s) => s.status === "planned" || s.status === "proposed",
  ).length;

  return (
    <div className="mt-6 border-t border-base-300 pt-5">
      <div className="mb-3">
        <h3 className="text-lg font-semibold text-base-content font-heading">
          {date.toLocaleDateString("en-GB", {
            weekday: "long",
            day: "numeric",
            month: "long",
          })}
        </h3>
        <div className="mt-1 flex flex-wrap gap-1.5">
          <span className="inline-flex items-center rounded-full bg-base-300/70 px-2.5 py-0.5 text-xs font-medium text-base-content/60 font-grotesk">
            <span className="font-geist tabular-nums mr-1">{plannedCount}</span>
            planned
          </span>
          {loggedCount > 0 && (
            <span className="inline-flex items-center rounded-full bg-success/15 px-2.5 py-0.5 text-xs font-medium text-success font-grotesk">
              <span className="font-geist tabular-nums mr-1">{loggedCount}</span>
              logged
            </span>
          )}
        </div>
      </div>

      {(sessions.length > 0 || events.length > 0 || recoveries.length > 0) && (
        <div className="space-y-2">
          {recoveries.map((item) => (
            <RecoveryScheduleCard
              key={item.id}
              item={item}
              onClick={onSelectRecovery ? () => onSelectRecovery(item) : undefined}
            />
          ))}
          {events.map((comp) => (
            <EventCard
              key={`comp-${comp.id}`}
              event={comp}
              dateKey={getLocalDateString(date)}
              variant="compact"
              logged={isEventLogged?.(comp.id) ?? false}
              onClick={
                onSelectEvent ? () => onSelectEvent(comp.id) : undefined
              }
            />
          ))}
          {sessions.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              trainingTypeColors={trainingTypeColors}
              variant="compact"
              onClick={() => onSelectSession(session.id)}
              onDelete={() => onDeleteSession(session.id)}
              onChangeDate={
                canChangeDate(session.id)
                  ? () => onChangeDate(session.id)
                  : undefined
              }
            />
          ))}
        </div>
      )}

      <button
        onClick={onAssign}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-semibold text-primary-content hover:bg-primary/90 transition-colors font-grotesk"
      >
        <HiPlus className="w-4 h-4" />
        Assign a session
      </button>
    </div>
  );
};

export default DayDetail;
